"use client";

import { c as _c } from "react/compiler-runtime";
import posthog from "posthog-js";
import { PostHogProvider as PHProvider, usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useAuthStore } from "@/lib/store";
import { safeGetItem } from "@/lib/safeStorage";
import { ApiError } from "@/lib/api";

// Expected, in-band-handled API responses that must NOT land in error tracking:
//   401 → session expired, api.ts already forces logout (normal flow)
//   403 → quota / upgrade-required / email-not-verified gates (business responses)
//   429 → rate limited (the app toasts + backs off)
// lib/errors.ts (reportSettledRejections / handleApiError) already skips these for
// CAUGHT errors; the global window handlers below are the gap where an UNHANDLED
// rejection slips through and shows up as a fake "ApiError: Not authenticated"
// issue (prod: 38+ occurrences). 400/422 and 5xx still get captured.
function isExpectedApiError(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 401 || err.status === 403 || err.status === 429);
}
const COOKIE_CONSENT_KEY = "velyq-cookie-consent";

/**
 * Detect if the referrer for the current session is an AI assistant. Returns
 * the engine name (chatgpt, claude, perplexity, gemini, copilot, you, grok)
 * or null. Used to track AEO/GEO traffic in PostHog.
 */
function detectAiReferrer(): string | null {
  if (typeof document === "undefined" || !document.referrer) return null;
  let host = "";
  try {
    host = new URL(document.referrer).hostname.toLowerCase();
  } catch {
    return null;
  }
  // Each entry: known AI agent host fragment → engine label
  const MAP: [string, string][] = [["chat.openai.com", "chatgpt"], ["chatgpt.com", "chatgpt"], ["claude.ai", "claude"], ["perplexity.ai", "perplexity"], ["gemini.google.com", "gemini"], ["bard.google.com", "gemini"], ["copilot.microsoft.com", "copilot"], ["you.com", "you"], ["grok.com", "grok"], ["grok.x.com", "grok"], ["duckduckgo.com/?q=", "duckassist"],
  // imperfect; assist embedded in search
  ["meta.ai", "meta-ai"], ["mistral.ai", "lechat"]];
  for (const [needle, engine] of MAP) {
    if (host.includes(needle.split("/")[0])) return engine;
  }
  return null;
}
const AI_REFERRER_FIRED_KEY = "velyq-ai-referrer-fired";
function PostHogPageView() {
  const $ = _c(5);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const posthogClient = usePostHog();
  let t0;
  let t1;
  if ($[0] !== pathname || $[1] !== posthogClient || $[2] !== searchParams) {
    t0 = () => {
      if (pathname && posthogClient) {
        let url = window.origin + pathname;
        if (searchParams.toString()) {
          url = url + "?" + searchParams.toString();
        }
        posthogClient.capture("$pageview", {
          $current_url: url
        });
        try {
          if (!sessionStorage.getItem(AI_REFERRER_FIRED_KEY)) {
            const engine = detectAiReferrer();
            if (engine) {
              posthogClient.capture("ai_referrer_detected", {
                ai_engine: engine,
                referrer: document.referrer,
                landing_url: url
              });
              sessionStorage.setItem(AI_REFERRER_FIRED_KEY, "1");
            }
          }
        } catch {}
      }
    };
    t1 = [pathname, searchParams, posthogClient];
    $[0] = pathname;
    $[1] = posthogClient;
    $[2] = searchParams;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  return null;
}
function PostHogIdentify() {
  const $ = _c(5);
  const {
    user,
    isAuthenticated
  } = useAuthStore();
  const posthogClient = usePostHog();
  let t0;
  let t1;
  if ($[0] !== isAuthenticated || $[1] !== posthogClient || $[2] !== user) {
    t0 = () => {
      if (!posthogClient) {
        return;
      }
      if (isAuthenticated && user) {
        posthogClient.identify(user.id || user.email, {
          email: user.email,
          name: [user.first_name, user.last_name].filter(Boolean).join(" "),
          plan: user.plan,
          role: user.role
        });
      } else {
        posthogClient.reset();
      }
    };
    t1 = [isAuthenticated, user, posthogClient];
    $[0] = isAuthenticated;
    $[1] = posthogClient;
    $[2] = user;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  return null;
}
export function PostHogProviderWrapper({
  children
}: {
  children: React.ReactNode;
}) {
  const [hasConsent, setHasConsent] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const apiKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  const apiHost = process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://eu.i.posthog.com";
  useEffect(() => {
    const consent = safeGetItem(COOKIE_CONSENT_KEY);
    if (consent === "accepted") {
      setHasConsent(true);
    }
    const interval = setInterval(() => {
      const c = safeGetItem(COOKIE_CONSENT_KEY);
      if (c === "accepted") {
        setHasConsent(true);
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    if (!apiKey || !hasConsent || initialized) return;
    posthog.init(apiKey, {
      api_host: apiHost,
      person_profiles: "identified_only",
      capture_pageview: false,
      // We handle this manually
      capture_pageleave: true,
      autocapture: true,
      session_recording: {
        // Mask ALL inputs in replays. Velyq handles deeply personal content
        // (CVs, cover letters, interview answers) — leaving them visible in
        // replays is a major GDPR exposure. Unmask specific elements case-by-case
        // via `data-ph-no-mask` attributes on the input element if ever needed.
        maskAllInputs: true,
        maskInputOptions: {
          password: true
        }
      }
    });
    setInitialized(true);
  }, [apiKey, apiHost, hasConsent, initialized]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onError = (e: ErrorEvent) => {
      if (isExpectedApiError(e.error)) return; // handled in-band, not a real error
      try {
        posthog.captureException(e.error ?? new Error(e.message), {
          kind: "window.onerror",
          filename: e.filename,
          lineno: e.lineno,
          colno: e.colno
        });
      } catch {
        /* fail soft */
      }
    };
    const onRejection = (e_0: PromiseRejectionEvent) => {
      if (isExpectedApiError(e_0.reason)) return; // expected 401/403/429, handled in-band
      const reason = e_0.reason instanceof Error ? e_0.reason : new Error(String(e_0.reason));
      try {
        posthog.captureException(reason, {
          kind: "unhandledrejection"
        });
      } catch {
        /* fail soft */
      }
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);

    // Mirror console.warn to PostHog (no sampling — relies on PostHog dedup).
    // Do NOT wrap console.error: React + Next.js already log to console.error for
    // caught errors, and our error.tsx boundaries handle the unhandled ones.
    // Wrapping would double-fire on every React-caught error.
    const originalWarn = console.warn;
    // Reentry guard: PostHog SDK may itself call console.warn (queue full,
    // network retry). Without this flag, our wrapper recurses and amplifies.
    let inWarnWrap = false;
    console.warn = (...args: unknown[]) => {
      originalWarn(...args);
      if (inWarnWrap) return;
      inWarnWrap = true;
      try {
        const message = args.map(a => {
          if (typeof a === "string") return a;
          if (a instanceof Error) return a.message;
          try {
            return JSON.stringify(a);
          } catch {
            return String(a);
          }
        }).join(" ").slice(0, 500);
        posthog.capture("$warning", {
          message,
          source: "frontend.console.warn"
        });
      } catch {
        /* fail soft */
      } finally {
        inWarnWrap = false;
      }
    };
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      console.warn = originalWarn;
    };
  }, []);
  if (!apiKey || !initialized) {
    return <>{children}</>;
  }
  return <PHProvider client={posthog}>
      <PostHogPageView />
      <PostHogIdentify />
      {children}
    </PHProvider>;
}